import {
  Button,
  Card,
  Checkbox,
  Container,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField,
  Typography
} from "@material-ui/core";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import KeyboardArrowLeftIcon from "@material-ui/icons/KeyboardArrowLeft";
import FormSpacer from "@saleor/components/FormSpacer";
import {
  useDropOffLocationCreate,
  useDropOffLocationUpdate
} from "@saleor/dropOff/mutation";
import { useGetTemplateQuery } from "@saleor/dropOff/queries";
import { useTemplateStyles } from "@saleor/dropOff/styles/templates";
import { GetTemplate_template_dropOffLocations_edges_node } from "@saleor/dropOff/types/GetTemplate";
import {
  TemplateAction,
  userCanManageHomeLocations,
  userCanManageTaskAreas
} from "@saleor/dropOff/utils/handlers";
import useNotifier from "@saleor/hooks/useNotifier";
import useUser from "@saleor/hooks/useUser";
import { maybe } from "@saleor/misc";
import {
  DropOffLocationSortField,
  LogisticsStatusEnum,
  OrderDirection
} from "@saleor/types/globalTypes";
import { handleGraphQLErrors } from "@saleor/utils/handlers/errorHandler";
import * as React from "react";
import { useIntl } from "react-intl";

import { checkLetterOnly, checkNumberOnly } from "../../utils/validation";
import DropOffHomeLocationList from "./DropOffHomeLocationList";

interface DropOffManageHomeLocationProps {
  mallId: string;
  templateId: string;
  templateAction: TemplateAction;
  templateDefaultHomeLoc: GetTemplate_template_dropOffLocations_edges_node;
  setHomeLocations: (any) => void;
}

const DropOffManageHomeLocation: React.FC<DropOffManageHomeLocationProps> = props => {
  const { mallId, templateId, templateAction, templateDefaultHomeLoc } = props;

  const classes = useTemplateStyles(props);
  const notify = useNotifier();
  const intl = useIntl();
  const user = useUser();

  const [hideForm, setHideForm] = React.useState(false);
  const [homeLocation, setHomeLocation] = React.useState("");
  const [prefix, setPrefix] = React.useState("");
  const [latitude, setLatitude] = React.useState("");
  const [longitude, setLongitude] = React.useState("");
  const [firstName, setFirstName] = React.useState("");
  const [lastName, setLastName] = React.useState("");
  const [contactNo, setContactNo] = React.useState("");
  const [dropOffAddress, setDropOffAddress] = React.useState("");
  const [remarks, setRemarks] = React.useState("");
  const [defaultDropOffLocation, setDefaultDropOffLocation] = React.useState(
    false
  );
  const [homeLocations, setHomeLocations] = React.useState<
    GetTemplate_template_dropOffLocations_edges_node[]
  >([]);
  const [selectedHomeLoc, setSelectedHomeLoc] = React.useState<
    GetTemplate_template_dropOffLocations_edges_node
  >(null);
  const [highlightHomeLoc, setHighlightHomeLoc] = React.useState<
    GetTemplate_template_dropOffLocations_edges_node
  >(null);
  const [forEditHomeLoc, setForEditHomeLoc] = React.useState(false);

  const { data: template, refetch } = useGetTemplateQuery({
    displayLoader: true,
    skip: !templateId || templateId.length === 0,
    variables: {
      dropOffSortBy: {
        direction: OrderDirection.DESC,
        field: DropOffLocationSortField.UPDATED_DATE
      },
      first: 100,
      id: templateId
    }
  });

  const isFormRequiredMissing = () =>
    !homeLocation ||
    (homeLocation && homeLocation.trim().length === 0) ||
    !prefix ||
    (prefix && prefix.trim().length === 0) ||
    !latitude ||
    (latitude && latitude.trim().length === 0) ||
    !longitude ||
    (longitude && longitude.trim().length === 0) ||
    firstName === "" ||
    lastName === "" ||
    contactNo === "" ||
    dropOffAddress === "";

  const clearForm = (clearFieldsOnly: boolean = false) => {
    if (!clearFieldsOnly) {
      setSelectedHomeLoc(null);
      if (templateDefaultHomeLoc?.id === undefined) {
        setHighlightHomeLoc(templateDefaultHomeLoc);
      } else {
        const getDefault = homeLocations.find(
          h => h.defaultDropOffLocation === true
        );
        if (getDefault?.id) {
          setHighlightHomeLoc(getDefault);
        }
      }
      setForEditHomeLoc(false);
    }

    setHomeLocation("");
    setPrefix("");
    setLatitude("");
    setLongitude("");
    setFirstName("");
    setLastName("");
    setContactNo("");
    setDropOffAddress("");
    setRemarks("");
    setDefaultDropOffLocation(false);
  };

  const refetchHomeLocations = () => {
    refetch();
  };

  const loadHomeLocation = () => {
    if (templateDefaultHomeLoc) {
      setSelectedHomeLoc(templateDefaultHomeLoc);
      setHighlightHomeLoc(templateDefaultHomeLoc);
      setForEditHomeLoc(true);

      setHomeLocation(templateDefaultHomeLoc.name);
      setPrefix(templateDefaultHomeLoc.code);
      setLatitude(templateDefaultHomeLoc.latitude.toString());
      setLongitude(templateDefaultHomeLoc.longitude.toString());
      setFirstName(templateDefaultHomeLoc.firstName);
      setLastName(templateDefaultHomeLoc.lastName);
      setContactNo(templateDefaultHomeLoc.contactNo.substring(2));
      setDropOffAddress(templateDefaultHomeLoc.dropOffAddress);
      setRemarks(templateDefaultHomeLoc.remarks);
      setDefaultDropOffLocation(templateDefaultHomeLoc.defaultDropOffLocation);
    }
  };

  const editHomeLocationEvent = homeLoc => {
    if (homeLoc) {
      setSelectedHomeLoc(homeLoc);
      setHighlightHomeLoc(homeLoc);
      setForEditHomeLoc(true);

      setHomeLocation(homeLoc.name);
      setPrefix(homeLoc.code);
      setLatitude(homeLoc.latitude.toString());
      setLongitude(homeLoc.longitude.toString());
      setFirstName(homeLoc.firstName);
      setLastName(homeLoc.lastName);
      setContactNo(homeLoc.contactNo.substring(2));
      setDropOffAddress(homeLoc.dropOffAddress);
      setRemarks(homeLoc.remarks);
      setDefaultDropOffLocation(homeLoc.defaultDropOffLocation);
    }
  };

  const addHomeLoc = () => {
    clearForm();
    setForEditHomeLoc(false);
  };

  const isHomeLocationFormValid = (isFromUpdate: boolean) => {
    if (!isFromUpdate && homeLocations.length === 6) {
      notify({
        status: "error",
        text: "The maximum number of home locations has been reached"
      });
      return false;
    }

    if (
      (contactNo.length > 0 && contactNo.length < 10) ||
      !contactNo.match(/^[0-9]+$/)
    ) {
      notify({
        status: "error",
        text: "Invalid Contact Number"
      });
      return false;
    }

    const homeLocWithSameName = homeLocations.find(
      val => val.name === homeLocation
    );
    const conditionToCheck = isFromUpdate
      ? homeLocWithSameName && selectedHomeLoc.id !== homeLocWithSameName.id
      : homeLocWithSameName;
    if (conditionToCheck) {
      notify({
        status: "error",
        text: "A Home Location with this name already exist"
      });
      return false;
    }

    return true;
  };

  const [createDropOffLocation] = useDropOffLocationCreate({
    onCompleted: e => {
      if (e.dropOffLocationCreate.dropOffLocationError.length === 0) {
        notify({
          status: "success",
          text: "All changes saved"
        });
        clearForm();
        refetchHomeLocations();
      } else {
        notify({
          status: "error",
          text: e.dropOffLocationCreate.dropOffLocationError[0].message
        });
      }
    },
    onError: handleGraphQLErrors
  });

  const clickAddHomeLocation = () => {
    if (!isHomeLocationFormValid(false)) {
      return;
    }

    createDropOffLocation({
      variables: {
        input: {
          code: prefix,
          contactNo: "63" + contactNo,
          defaultDropOffLocation,
          dropOffAddress,
          firstName,
          lastName,
          latitude,
          longitude,
          mall: mallId,
          name: homeLocation,
          remarks,
          status: LogisticsStatusEnum.ACTIVE,
          templateId
        }
      }
    });
  };

  const [updateDropOffLocation] = useDropOffLocationUpdate({
    onCompleted: e => {
      if (e.dropOffLocationUpdate.dropOffLocationError.length === 0) {
        notify({
          status: "success",
          text: "All changes saved"
        });
        clearForm();
        refetchHomeLocations();
      } else {
        notify({
          status: "error",
          text: e.dropOffLocationUpdate.dropOffLocationError[0].message
        });
      }
    },
    onError: handleGraphQLErrors
  });

  const clickSaveChanges = () => {
    if (!isHomeLocationFormValid(true)) {
      return;
    }

    updateDropOffLocation({
      variables: {
        id: selectedHomeLoc.id,
        input: {
          code: prefix,
          contactNo: "63" + contactNo,
          defaultDropOffLocation,
          dropOffAddress,
          firstName,
          lastName,
          latitude: latitude ? latitude.toString() : "",
          longitude: longitude ? longitude.toString() : "",
          mall: mallId,
          name: homeLocation,
          remarks
        }
      }
    });
  };

  React.useEffect(() => {
    if (template?.template?.dropOffLocations?.edges) {
      const homeLocs = template?.template?.dropOffLocations?.edges.map(
        edge => edge.node
      );
      setHomeLocations(homeLocs);
    }
  }, [template?.template?.dropOffLocations]);

  React.useEffect(() => {
    if (homeLocations && homeLocations.length > 0) {
      props.setHomeLocations(homeLocations);
    }
  }, [homeLocations]);

  React.useEffect(() => {
    loadHomeLocation();
  }, [templateDefaultHomeLoc]);

  const showSetActive = () => {
    let _showSetActive = false;

    if (templateAction === TemplateAction.CREATE) {
      if (selectedHomeLoc?.defaultDropOffLocation !== undefined) {
        _showSetActive = !selectedHomeLoc.defaultDropOffLocation;
      } else {
        _showSetActive = homeLocations && homeLocations.length > 0;
      }
    } else {
      _showSetActive = !selectedHomeLoc?.defaultDropOffLocation;
    }

    return _showSetActive;
  };

  const getTitle = () => {
    if (templateAction === TemplateAction.CREATE) {
      return "Create Home Location";
    } else if (forEditHomeLoc && templateAction === TemplateAction.EDIT) {
      return "Edit Home Location";
    } else if (templateAction === TemplateAction.VIEW) {
      return "View Home Location";
    } else {
      return "Add Home Location";
    }
  };

  return (
    <Container>
      <Card className={classes.dropOffCardAccordion}>
        <div className={classes.dropOffCardAccordionBody}>
          <Grid container spacing={2}>
            <Grid item xs={10}>
              <Typography
                variant="h5"
                className={classes.dropOffCardAccordionTitle}
              >
                {getTitle()}
              </Typography>
            </Grid>
            <Grid
              item
              xs={2}
              className={classes.circleContainer}
              onClick={() => {
                setHideForm(!hideForm);
              }}
            >
              <div className={classes.circle}>
                {hideForm ? (
                  <KeyboardArrowLeftIcon
                    fontSize="large"
                    className={`${classes.textWhite} ${classes.textFont30}`}
                  />
                ) : (
                  <KeyboardArrowDownIcon
                    fontSize="large"
                    className={`${classes.textWhite} ${classes.textFont30}`}
                  />
                )}
              </div>
            </Grid>
          </Grid>

          <FormSpacer spacing={2} />
          <Divider variant="fullWidth" />
          <FormSpacer spacing={2} />

          {!hideForm && (
            <>
              {/* HOME LOCATION FORM */}
              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <TextField
                    disabled={templateAction === TemplateAction.VIEW}
                    required
                    value={homeLocation}
                    label={intl.formatMessage({
                      defaultMessage: "Home Location Name"
                    })}
                    fullWidth
                    onChange={event => {
                      if (event.target.value.length > 30) {
                        return;
                      }
                      setHomeLocation(event.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    disabled={templateAction === TemplateAction.VIEW}
                    required
                    value={prefix}
                    label={intl.formatMessage({
                      defaultMessage: "Prefix"
                    })}
                    fullWidth
                    onKeyDown={checkLetterOnly}
                    onChange={event => {
                      if (event.target.value.length > 1) {
                        return;
                      }
                      setPrefix(event.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    disabled={templateAction === TemplateAction.VIEW}
                    required
                    value={latitude}
                    label={intl.formatMessage({
                      defaultMessage: "Latitude"
                    })}
                    fullWidth
                    type="number"
                    onChange={event => {
                      if (event.target.value.length > 30) {
                        return;
                      }
                      setLatitude(event.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    disabled={templateAction === TemplateAction.VIEW}
                    required
                    value={longitude}
                    label={intl.formatMessage({
                      defaultMessage: "Longitude"
                    })}
                    fullWidth
                    type="number"
                    onChange={event => {
                      if (event.target.value.length > 30) {
                        return;
                      }
                      setLongitude(event.target.value);
                    }}
                  />
                </Grid>
              </Grid>

              <FormSpacer spacing={2} />

              {/* CONTACT DETAILS */}
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <Typography
                    variant="h6"
                    className={classes.dropOffCardAccordionSmallTitle}
                  >
                    CONTACT DETAILS
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    disabled={templateAction === TemplateAction.VIEW}
                    required
                    value={firstName}
                    label={intl.formatMessage({
                      defaultMessage: "First Name"
                    })}
                    fullWidth
                    onChange={event => {
                      if (event.target.value.length > 30) {
                        return;
                      }
                      setFirstName(event.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    disabled={templateAction === TemplateAction.VIEW}
                    required
                    value={lastName}
                    label={intl.formatMessage({
                      defaultMessage: "Last Name"
                    })}
                    fullWidth
                    onChange={event => {
                      if (event.target.value.length > 30) {
                        return;
                      }
                      setLastName(event.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    disabled={templateAction === TemplateAction.VIEW}
                    required
                    value={
                      templateAction === TemplateAction.VIEW
                        ? `+63${contactNo}`
                        : contactNo
                    }
                    label={intl.formatMessage({
                      defaultMessage: "Contact Number"
                    })}
                    fullWidth
                    onKeyDown={checkNumberOnly}
                    onChange={event => {
                      if (event.target.value.length > 10) {
                        return;
                      }
                      setContactNo(event.target.value);
                    }}
                    InputProps={
                      templateAction === TemplateAction.VIEW
                        ? {}
                        : {
                            startAdornment: (
                              <InputAdornment
                                position="start"
                                style={{ paddingTop: "15px" }}
                              >
                                +63
                              </InputAdornment>
                            )
                          }
                    }
                  />
                </Grid>
                <Grid item xs={6}>
                  <TextField
                    disabled={templateAction === TemplateAction.VIEW}
                    required
                    value={dropOffAddress}
                    label={intl.formatMessage({
                      defaultMessage: "Address Line Based on Pin"
                    })}
                    fullWidth
                    onChange={event => {
                      if (event.target.value.length > 255) {
                        return;
                      }
                      setDropOffAddress(event.target.value);
                    }}
                  />
                </Grid>
                <Grid item xs={12}>
                  <TextField
                    disabled={templateAction === TemplateAction.VIEW}
                    multiline
                    rows={4}
                    value={remarks}
                    label={intl.formatMessage({
                      defaultMessage: "Remarks"
                    })}
                    fullWidth
                    onChange={event => {
                      if (event.target.value.length > 255) {
                        return;
                      }
                      setRemarks(event.target.value);
                    }}
                  />
                </Grid>
              </Grid>

              {/* SET AS DEFAULT */}
              {showSetActive() && templateAction !== TemplateAction.VIEW ? (
                <div className={classes.textFieldGrid}>
                  <form noValidate autoComplete="off">
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={defaultDropOffLocation}
                          onChange={e => {
                            setDefaultDropOffLocation(e.target.checked);
                          }}
                          color="primary"
                        />
                      }
                      label="Set as default Home Location"
                    />
                  </form>
                </div>
              ) : (
                <></>
              )}

              <FormSpacer spacing={2} />
              {templateAction !== TemplateAction.VIEW && (
                <div className={classes.displayFlex}>
                  {templateAction === TemplateAction.CREATE ||
                  (templateAction === TemplateAction.EDIT &&
                    !forEditHomeLoc) ? (
                    <Button
                      className={classes.buttonColorGray}
                      variant="outlined"
                      style={{
                        marginLeft: "auto"
                      }}
                      onClick={() => clearForm(false)}
                      disabled={!userCanManageHomeLocations(user?.user)}
                    >
                      <span>CANCEL</span>
                    </Button>
                  ) : (
                    <Button
                      className={classes.buttonColorGray}
                      variant="outlined"
                      style={{
                        marginLeft: "auto"
                      }}
                      onClick={() => clearForm(true)}
                      disabled={!userCanManageHomeLocations(user?.user)}
                    >
                      <span>CLEAR</span>
                    </Button>
                  )}

                  {(templateAction === TemplateAction.EDIT ||
                    templateAction === TemplateAction.CREATE) && (
                    <>
                      {maybe(() => forEditHomeLoc) ? (
                        <Button
                          className={classes.buttonColorYellow}
                          variant="contained"
                          color="primary"
                          style={{
                            marginLeft: 20
                          }}
                          onClick={clickSaveChanges}
                          disabled={
                            isFormRequiredMissing() ||
                            !userCanManageHomeLocations(user?.user)
                          }
                        >
                          <span>SAVE CHANGES</span>
                        </Button>
                      ) : (
                        <Button
                          className={classes.buttonColorYellow}
                          variant="contained"
                          color="primary"
                          style={{
                            marginLeft: 20
                          }}
                          onClick={clickAddHomeLocation}
                          disabled={
                            isFormRequiredMissing() ||
                            !userCanManageHomeLocations(user?.user)
                          }
                        >
                          <span>ADD</span>
                        </Button>
                      )}
                    </>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </Card>
      <FormSpacer spacing={3} />
      {templateAction === TemplateAction.EDIT && (
        <Button
          onClick={addHomeLoc}
          disabled={!forEditHomeLoc || !userCanManageHomeLocations(user?.user)}
        >
          <Typography
            variant="inherit"
            className={
              !forEditHomeLoc || !userCanManageHomeLocations(user?.user)
                ? classes.textGray
                : classes.textPrimary
            }
          >
            + Add Home Location
          </Typography>
        </Button>
      )}
      <FormSpacer spacing={4} />

      {/* SAVED HOME LOCATIONS LIST */}
      <DropOffHomeLocationList
        key={"hlList" + mallId + templateId}
        mallId={mallId}
        templateId={templateId}
        homeLocations={homeLocations}
        clearForm={clearForm}
        refetch={refetchHomeLocations}
        editHomeLocationEvent={editHomeLocationEvent}
        highlightHomeLoc={highlightHomeLoc}
        templateAction={templateAction}
        userCanManageHomeLocations={userCanManageHomeLocations(user?.user)}
        userCanManageTaskAreas={userCanManageTaskAreas(user?.user)}
      />
    </Container>
  );
};

export default DropOffManageHomeLocation;
